import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { Ornament } from "@/components/Ornament";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = ["Wedding", "Private Gala", "Festival", "Concert / Venue Show", "Silent Film Score", "Corporate Event", "Other"];
const BUDGETS = ["Under $2,500", "$2,500 – $5,000", "$5,000 – $10,000", "$10,000+", "Not sure yet"];

const initial = {
  name: "", email: "", phone: "", event_type: EVENT_TYPES[0],
  event_date: "", venue: "", guests: "", budget: BUDGETS[4], message: "",
};

const INCLUDED = [
  "Configurations from an intimate quartet to a larger ensemble",
  "Instrumentation: clarinet, accordion, guitar, bass, dumbeg and vocals",
  "Custom sets for ceremonies, dinners and dance floors",
  "Fully self-contained sound available on request",
];

export default function Booking() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and a few words about the event.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        guests: form.guests ? Number(form.guests) : null,
      });
      toast.success("Inquiry received — the ensemble will reply within 48 hours.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong sending your inquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-32">
      <Seo
        title="Booking & Inquiries — 1001 Nights Orchestra"
        siteName="1001 Nights Orchestra"
        description="Book 1001 Nights Orchestra for weddings, festivals, galas and silent-film scores. Austin, TX based; touring across Texas and beyond."
      />
      <header className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-14">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-amber-400">Booking & Inquiries</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-amber-50 tracking-tight">
            Bring the night <span className="italic gold-text">to your stage</span>
          </h1>
        </Reveal>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <h2 className="font-serif text-2xl text-amber-100 mb-6">What to expect</h2>
          <ul className="space-y-4">
            {INCLUDED.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                <Check size={15} className="mt-0.5 shrink-0 text-gold" />
                {item}
              </li>
            ))}
          </ul>
          <Ornament className="my-10" />
          <div className="space-y-5 text-sm text-slate-400 leading-relaxed">
            <p>
              <span className="text-amber-200 font-medium">Response time.</span> Every inquiry is
              read by the band directly — expect a reply within 48 hours with availability
              and a tailored quote.
            </p>
            <p>
              <span className="text-amber-200 font-medium">Where we play.</span> Based in Austin,
              Texas; regularly performing across Texas and available for travel.
            </p>
            <p>
              <span className="text-amber-200 font-medium">Helpful details.</span> Date, venue,
              guest count and the mood you're after help us shape the right ensemble for you.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="booking-form"
            className="border border-amber-500/15 bg-surface/60 p-6 sm:p-10 space-y-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="bk-name" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400 mb-2">Name *</label>
                <input id="bk-name" data-testid="booking-name-input" className="field" value={form.name} onChange={set("name")} placeholder="Your full name" />
              </div>
              <div>
                <label htmlFor="bk-email" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400 mb-2">Email *</label>
                <input id="bk-email" type="email" data-testid="booking-email-input" className="field" value={form.email} onChange={set("email")} placeholder="you@email.com" />
              </div>
              <div>
                <label htmlFor="bk-phone" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400 mb-2">Phone</label>
                <input id="bk-phone" data-testid="booking-phone-input" className="field" value={form.phone} onChange={set("phone")} placeholder="(512) 555-0100" />
              </div>
              <div>
                <label htmlFor="bk-type" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400 mb-2">Event Type</label>
                <select id="bk-type" data-testid="booking-event-type-select" className="field" value={form.event_type} onChange={set("event_type")}>
                  {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="bk-date" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400 mb-2">Event Date</label>
                <input id="bk-date" type="date" data-testid="booking-date-input" className="field [color-scheme:dark]" value={form.event_date} onChange={set("event_date")} />
              </div>
              <div>
                <label htmlFor="bk-venue" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400 mb-2">Venue / City</label>
                <input id="bk-venue" data-testid="booking-venue-input" className="field" value={form.venue} onChange={set("venue")} placeholder="e.g. The Long Center, Austin" />
              </div>
              <div>
                <label htmlFor="bk-guests" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400 mb-2">Guest Count</label>
                <input id="bk-guests" type="number" min="1" data-testid="booking-guests-input" className="field" value={form.guests} onChange={set("guests")} placeholder="Approximate" />
              </div>
              <div>
                <label htmlFor="bk-budget" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400 mb-2">Budget Range</label>
                <select id="bk-budget" data-testid="booking-budget-select" className="field" value={form.budget} onChange={set("budget")}>
                  {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="bk-message" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400 mb-2">Tell Us About the Night *</label>
              <textarea
                id="bk-message"
                data-testid="booking-message-input"
                rows={5}
                className="field resize-none"
                value={form.message}
                onChange={set("message")}
                placeholder="The occasion, the mood, any must-hear pieces..."
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="booking-submit-btn"
              className="group inline-flex items-center gap-2 px-8 py-4 bg-gold text-midnight font-mono text-xs uppercase tracking-[0.2em] hover:bg-gold-bright transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sending ? "Sending..." : "Send Inquiry"}
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>
      </section>
    </main>
  );
}
